import React, { useCallback, useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, Drawer, Grid, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  formatISTtoTime,
  getTimeDifferenceInMinutes
} from '../../../../../utils/commonFunctions';
import dayjs, { Dayjs } from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  autoGenerateUpdateTripsAction,
  getStandardVehicles
} from '../../../redux/reducer/autoPlannerSlices/autoGenerateSlice';
import CustomRadioButton from '../../../../../common/components/customized/customradiobutton/CustomRadioButton';
import CustomSelect from '../../../../../common/components/customized/customselect/CustomSelect';
import CustomTextField from '../../../../../common/components/customized/customtextfield/CustomTextField';
import PhoneNoTextField from '../../../../../common/components/customized/customtextfield/PhoneNoTextField';
import CustomTimePicker from '../../../../../common/components/customized/customtimepicker/CustomTimePicker';
import CustomButton from '../../../../../common/components/buttons/CustomButton';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import { debounce, toUpper } from 'lodash';
import { contactNumberValidation } from '../../../redux/reducer/autoPlannerSlices/autoplanner';

interface Props {
  isUpdate: boolean;
  setIsUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRow: any;
  autoplannerID: any;
  tripMode?: any;
  filterPayload?: any;
}

const UpdateScheduleTrip = ({
  isUpdate,
  setIsUpdate,
  selectedRow,
  filterPayload,
  autoplannerID,
  tripMode
}: Props) => {
  const [minPickTime, setMinPickTime] = useState<Dayjs | undefined>(undefined);
  const [maxPickTime, setMaxPickTime] = useState<Dayjs | undefined>(undefined);
  const [external, setExternal] = useState<boolean>(true);
  const [timeDif, setTimeDif] = useState<number>(0);
  const [validationErrors, setValidationErrorsMake] = useState<any>({
    driverNumber: 'no-error'
  });

  const dispatch = useAppDispatch();

  const Routes = useAppSelector(state => state.autoPlannerRoutes.data || []);
  const modeVehicle = useAppSelector(state => state.modeVehicle.data || []);
  const update = useAppSelector(state => state.AutoGeneratedUpdateTrip || []);
  const { isLoading } = useAppSelector(state => state.AutoGeneratedUpdateTrip);
  const reportData = useAppSelector(state => state.AutoGeneratedTrip);
  const { data, isLoading: dropdownLoading } = useAppSelector(
    state => state.getStandardVehicles
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tripSchema = (isReturn: boolean) =>
    yup.object().shape({
      vehicle: yup
        .string()
        .oneOf(['internal', 'external'])
        .required('Select vehicle type'),
      vehicleNumber: yup.string().when('vehicle', {
        is: 'internal',
        then: () => yup.string().required('Select vehicle number'),
        otherwise: () =>
          yup
            .string()
            .required('Enter vehicle number')
            .matches(/^[A-Za-z0-9]*$/, 'Only letters and numbers allowed without spaces')
            .test(
              'valid-length',
              'Enter a valid vehicle number',
              value => (value?.length || 0) >= 4
            )
            .max(20, 'Maximum 20 characters')
            .test('has-letter', 'Must include at least one letter', value =>
              /[A-Za-z]/.test(value || '')
            )
      }),
      driverName: yup.string().when('vehicle', {
        is: 'external',
        then: () => yup.string().required('Enter Driver Name'),
        otherwise: () => yup.string().notRequired()
      }),
      passengerCount: yup.string().when('vehicle', {
        is: 'external',
        then: () =>
          yup
            .number()
            .required('Enter Seat Capacity')
            .min(
              selectedRow?.netCount ? selectedRow?.netCount : 1,
              `Seat Capacity Must be Minimum ${selectedRow?.netCount}`
            )
            .typeError(`Seat Capacity Must be Minimum ${selectedRow?.netCount}`),
        otherwise: () => yup.string().notRequired()
      }),
      driverNumber: yup.string().when('vehicle', {
        is: 'external',
        then: schema =>
          schema
            .required('Enter driver contact number')
            .test(
              'contactNumber-error',
              'Enter a valid contact number',
              function (value: any) {
                if (value && validationErrors.driverNumber !== 'no-error') {
                  return this.createError({ message: validationErrors.driverNumber });
                } else {
                  return true;
                }
              }
            ),
        otherwise: () => yup.string().notRequired()
      }),

      picktime1: isReturn
        ? yup.date().notRequired()
        : yup
            .date()
            .required('Choose from time')
            .test('is-valid-time', 'Invalid time format', value => {
              return true || null;
            }),
      picktime2: !selectedRow?.isReturnTrip
        ? yup
            .date()
            .required('Choose to time')
            .test(
              'is-after-picktime1',
              'Pickup time to must be 30 min',
              function (value) {
                const { picktime1 } = this.parent;
                if (!picktime1 || !value) {
                  return true || null;
                }
                const pickTime1Date = new Date(picktime1);
                const pickTime2Date = new Date(value);
                return pickTime2Date >= pickTime1Date;
              }
            )
        : yup.date().notRequired(),
      returnTime: !isReturn
        ? yup.object().required('Choose Return Time')
        : yup.object().notRequired()
    });

  const standardVehicles =
    data?.length > 0
      ? data?.map((value: string, index: number) => ({
          id: value,
          label: value.toUpperCase()
        }))
      : [];

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
    reset,
    trigger,
    formState: { errors }
  } = useForm({
    defaultValues: {
      vehicle: 'internal'
    },
    resolver: yupResolver(tripSchema(selectedRow.isReturnTrip))
  });

  const handleValidate = useCallback(
    debounce(async (event: any, field: string) => {
      const response: any = await dispatch(contactNumberValidation(event));
      setValidationErrorsMake((prev: any) => ({
        ...prev,
        [field]: response?.payload?.status !== 200 ? response.payload : 'no-error'
      }));
    }, 200),
    []
  );

  const onSubmit = async (params: any) => {
    let payload: any = {
      id: selectedRow.id, //
      tourName: selectedRow.tourName, //
      pickupWindow: !selectedRow.returnTime
        ? `${formatISTtoTime(params?.picktime1)}-${formatISTtoTime(params?.picktime2)}`
        : null, //
      returnTime: selectedRow.returnTime ? formatISTtoTime(params.returnTime) : null,
      totalAdultCount: selectedRow.totalAdultCount, //
      totalChildCount: selectedRow.totalChildCount, //
      netCount: selectedRow.netCount, //
      isExternalVehicle: external ? 0 : 1, //
      vehicleNumber: params?.vehicleNumber || selectedRow.vehicleNumber, //
      isReturnTrip: selectedRow.isReturnTrip, //
      driverName: selectedRow.driverName, //
      driverID: selectedRow.driverID, //
      driverContactNumber: selectedRow.driverContactNumber, //
      modifyVehicleDriver: null, //
      externalVehicleView: external //
        ? null
        : {
            vehicleNumber: params?.vehicleNumber,
            driverName: params?.driverName,
            driverID: `${params?.driverName}-${params?.driverNumber}`,
            mobileNumber: params?.driverNumber,
            seatingCapacity: params.passengerCount
          }
    };
    const pageDetails = {
      autoplannerID: autoplannerID,
      tripMode: tripMode,
      ...filterPayload
    };

    const data = { payload, pageDetails };
    const action = await dispatch(autoGenerateUpdateTripsAction(data as any));
    if (action.type === autoGenerateUpdateTripsAction.fulfilled.type) {
      handleClose();
    }
  };

  const handleClose = () => {
    setValidationErrorsMake({ driverNumber: '' });
    reset();
    setIsUpdate(false);
  };

  const handleMinTime = (event: string, type: string) => {
    const time = formatISTtoTime(event);
    if (type === 'pickup') {
      setMinPickTime(
        dayjs()
          .set('hour', parseFloat(time.split(':')[0]))
          .set('minute', parseFloat(time.split(':')[1]) + 30)
      );
      setMaxPickTime(
        dayjs()
          .set('hour', parseFloat(time.split(':')[0]))
          .set('minute', parseFloat(time.split(':')[1]) + 30)
      );
    }
  };

  useEffect(() => {
    if (validationErrors.driverNumber) {
      trigger('driverNumber');
    }
  }, [validationErrors.driverNumber]);

  useEffect(() => {
    const payload = {
      autoplannerID: autoplannerID,
      tripMode: tripMode,
      tourName: selectedRow?.tourName,
      totalPassengers: selectedRow?.netCount,
      vehicleNumber: selectedRow?.vehicleNumber.toUpperCase(),
      journeyId: selectedRow?.journeyId ? selectedRow?.journeyId : ''
    };
    dispatch(getStandardVehicles(payload));
  }, []);

  useEffect(() => {
    if (selectedRow?.id) {
      const pickupWindow = selectedRow?.pickupWindow;
      if (pickupWindow) {
        const [startTime, endTime] = pickupWindow?.split('-');
        setTimeDif(getTimeDifferenceInMinutes(startTime, endTime));
        if (startTime && endTime) {
          setValue(
            'picktime1',
            dayjs()
              .set('hour', startTime?.split(':')[0])
              .set('minute', startTime?.split(':')[1])
              .toDate()
          );
          setValue(
            'picktime2',
            dayjs()
              .set('hour', endTime?.split(':')[0])
              .set('minute', endTime?.split(':')[1])
              .toDate()
          );
        }
        setValue(
          'vehicle',
          selectedRow.isExternalVehicle === 1 ? 'external' : 'internal'
        );
        if (selectedRow.isExternalVehicle === 1) {
          setExternal(false);
        }
        setValue('vehicleNumber', selectedRow.vehicleNumberUpper);
        setValue('driverName', selectedRow.driverName);
        setValue('passengerCount', selectedRow.seatingCapacity);
        setValue('driverNumber', selectedRow.driverContactNumber);
      }

      const returnTime =
        selectedRow?.returnTime !== null ? selectedRow?.returnTime : '00:00';
      setValue(
        'returnTime',
        dayjs()
          .set('hour', returnTime?.split(':')[0])
          .set('minute', returnTime?.split(':')[1])
      );

      setValue('vehicleNumber', selectedRow.vehicleNumberUpper);
    }
  }, [selectedRow]);

  useEffect(() => {
    if (getValues('picktime1')) {
      const initialPickTime: any = getValues('picktime1');
      handleMinTime(initialPickTime, 'pickup');
    }
  }, [getValues('picktime1')]);

  return (
    <>
      <Box className='add-trip-component'>
        <Box p={2}>
          <Drawer
            anchor='right'
            open={isUpdate}
            onClose={() => {
              reset();
              handleClose();
            }}
            sx={{ zIndex: 1 }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px !important'
              }}
              className='animate__animated animate__slideInUp'
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 15 }}
                className='maintenance-container'
              ></Box>

              <Grid item xs={12} className='add-trip-grid-item'>
                <Box className='add-trip-header'>
                  <Typography
                    className='add-trip-title'
                    sx={{ fontSize: '24 px', fontWeight: 600 }}
                  >
                    Update Tour
                  </Typography>
                </Box>
                <Box component={'form'} onSubmit={handleSubmit(onSubmit)}>
                  <Box>
                    <CustomRadioButton
                      name='vehicle'
                      control={control}
                      radiobutton1='Internal Vehicle'
                      radiobutton2='External Vehicle'
                      value={getValues('vehicle')}
                      value1='internal'
                      value2='external'
                      onChangeCallback={(e: any) => {
                        setValue('vehicle', e.target.value);
                        clearErrors();
                        if (e.target.value === 'internal') {
                          setExternal(true);
                          // setValue('vehicleNumber', selectedRow?.vehicleNumber);
                          setValue('vehicleNumber', '');
                          setValue('driverName', '');
                          setValue('passengerCount', '');
                          setValue('driverNumber', '');
                        } else {
                          setExternal(false);
                          // setValue('vehicleNumber', selectedRow?.vehicleNumber);
                          // setValue('driverName', selectedRow.driverName);
                          // setValue('passengerCount', selectedRow.seatingCapacity);
                          // setValue('driverNumber', selectedRow.driverContactNumber);
                          setValue('vehicleNumber', '');
                          setValue('driverName', '');
                          setValue('passengerCount', '');
                          setValue('driverNumber', '');
                        }
                      }}
                    />
                    {external ? (
                      <>
                        <CustomSelect
                          id='vehicleNumber'
                          control={control}
                          name='vehicleNumber'
                          label=' Vehicle Number'
                          placeholder='Select vehicle number'
                          options={standardVehicles}
                          loading={dropdownLoading}
                        />
                      </>
                    ) : (
                      <>
                        <Box>
                          <Box sx={{ marginBottom: '10px' }}>
                            <CustomTextField
                              id='vehicleNumber'
                              control={control}
                              name='vehicleNumber'
                              label='Vehicle Number'
                              placeholder='Enter external vehicle number'
                              onChangeCallback={e => {
                                setValue('vehicleNumber', e);
                              }}
                            />
                          </Box>
                          <Box sx={{ marginBottom: '10px' }}>
                            <CustomTextField
                              id='driverName'
                              control={control}
                              name='driverName'
                              label='Driver Name'
                              placeholder='Enter driver name'
                              onChangeCallback={e => {
                                setValue('driverName', e);
                              }}
                            />
                          </Box>
                          <Box sx={{ marginBottom: '10px' }}>
                            <CustomTextField
                              id='count'
                              control={control}
                              type='number'
                              name='passengerCount'
                              label='Seat Capacity'
                              placeholder='Count'
                              onChangeCallback={e => {
                                setValue('passengerCount', e);
                                clearErrors('passengerCount');
                              }}
                            />
                          </Box>
                          <Box sx={{ marginBottom: '10px' }}>
                            <Controller
                              name='driverNumber'
                              control={control}
                              rules={{ required: true }}
                              render={({ field }) => (
                                <PhoneNoTextField
                                  {...field}
                                  setValue={setValue}
                                  country={'sg'}
                                  style='share'
                                  label='Contact Number'
                                  error={Boolean(errors?.driverNumber?.message)}
                                  helperText={errors.driverNumber?.message}
                                  onChange={(e: any) => {
                                    if (e) {
                                      const emptySpace = /^.+\s.+$/g;

                                      if (e && !emptySpace.test(e)) {
                                        handleValidate(e, 'driverNumber');
                                      }
                                    } else {
                                      setValue('driverNumber', '');
                                    }
                                    trigger('driverNumber');
                                  }}
                                />
                              )}
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                  {Object.keys(selectedRow).includes('pickupWindow') ? (
                    <Box
                      sx={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#f9f9f9',
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <Typography sx={{ mb: 2, fontWeight: 'bold' }}>
                        PickUp Windows
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item sm={6}>
                          <CustomTimePicker
                            id='time'
                            name='picktime1'
                            control={control}
                            values={getValues('picktime1')}
                            label='From'
                            onTimeChange={(event: any) => {
                              if (event !== null && event.$d) {
                                handleMinTime(event.$d, 'pickup');
                                const startTime: any = formatISTtoTime(event.$d);
                                if (startTime)
                                  setValue(
                                    'picktime2',
                                    dayjs()
                                      .set('hour', startTime?.split(':')[0])
                                      .set('minute', startTime?.split(':')[1])
                                      .add(timeDif, 'minute')
                                      .toDate()
                                  );
                              }
                            }}
                          />
                        </Grid>
                        <Grid item sm={6}>
                          <CustomTimePicker
                            id='time'
                            name='picktime2'
                            control={control}
                            values={getValues('picktime2')}
                            label='To'
                            isDisabled={true}
                            minTime={minPickTime}
                            maxTime={maxPickTime}
                            onTimeChange={(event: any) => {
                              if (event !== null && event.$d)
                                handleMinTime(event.$d, 'drop');
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    <Box>
                      <Grid item sm={12}>
                        <CustomTimePicker
                          id='returnTime'
                          values={getValues('returnTime')}
                          name='returnTime'
                          control={control}
                          label='Return Time'
                        />
                      </Grid>
                    </Box>
                  )}

                  <Box mt={3} sx={{ textAlign: 'right' }}>
                    <CustomButton
                      category='Cancel'
                      className='cancel'
                      sx={{ marginRight: '10px' }}
                      onClick={() => handleClose()}
                    />
                    <CustomButton
                      category={'Save'}
                      className='saveChanges'
                      type='submit'
                      loading={isLoading}
                    />
                  </Box>
                </Box>
              </Grid>
            </Box>
          </Drawer>
        </Box>
      </Box>
    </>
  );
};

export default UpdateScheduleTrip;
